/**
 * Email store — sidebar slice.
 */
import {
  flattenSidebarCountsPayload,
  adjustCountKey,
  applyTagUpserted,
  applyTagRemoved,
  stripTagFromThread,
  stripTagFromThreads,
  applyFolderUpserted,
  applyFolderRemoved,
} from '@tshmanager/email-shared'
import {
  buildTagsPath,
  buildFoldersPath,
  buildBulkPath,
  buildEmailApiPath,
  buildSidebarCountsPath,
} from '@tshmanager/email-shared'
import { emailApiBase, normalizeListRow } from './api.js'

export function attachSidebar(ctx) {
  ctx.toggleTag = async function toggleTag(emailId, tagId) {
    const currentIds = (ctx.thread.value?.tags || []).map(t => Number(t.id))
      .concat((ctx.thread.value?.email_tag_ids || []).map(Number))
    const appliedBefore = currentIds.includes(Number(tagId))
    try {
      const data = await ctx.apiPost(
        buildEmailApiPath(emailApiBase(), 'tags', 'toggle'),
        { email_id: emailId, tag_id: tagId }
      )
      if (ctx.thread.value?.id === emailId && data.email_tag_ids != null) {
        const ids = data.email_tag_ids.map(Number)
        const allTags = ctx.emailTags.value || []
        ctx.thread.value = {
          ...ctx.thread.value,
          tags: allTags.filter(t => ids.includes(Number(t.id))),
          email_tag_ids: data.email_tag_ids,
        }
      }
      // Flat tag_<id> keys (same shape as sidebar_counts / EmailSidebar).
      const delta = appliedBefore ? -1 : +1
      ctx.sidebarCounts.value = adjustCountKey(ctx.sidebarCounts.value || {}, `tag_${tagId}`, delta)
    } catch (err) {
      console.error('[emailStore] toggleTag error:', err)
    }
  }

  ctx.createTag = async function createTag(tagName, tagColor, mailboxId) {
    const resolvedMailboxId = mailboxId ?? ctx.activeMailbox.value
    if (!resolvedMailboxId) {
      console.error('[emailStore] createTag: mailbox_id required')
      return null
    }

    const name = String(tagName || '').trim()
    if (!name) return null

    // Optimistic sidebar row so the create form can close immediately.
    // Temp ids are negative so they never collide with persisted ids.
    const tempId = -Date.now()
    const optimistic = {
      id: tempId,
      tag: name,
      color: tagColor || '#6c757d',
      mailbox_id: Number(resolvedMailboxId),
    }
    ctx.emailTags.value = applyTagUpserted(ctx.emailTags.value || [], optimistic)

    try {
      const data = await ctx.apiPost(
        buildTagsPath(emailApiBase()),
        { email_tag: { tag: name, color: optimistic.color, mailbox_id: resolvedMailboxId } }
      )
      if (data?.id) {
        ctx.emailTags.value = applyTagUpserted(
          applyTagRemoved(ctx.emailTags.value || [], tempId),
          {
            id: data.id,
            tag: data.tag,
            color: data.color,
            mailbox_id: data.mailbox_id,
          }
        )
        return data
      }
      ctx.emailTags.value = applyTagRemoved(ctx.emailTags.value || [], tempId)
      return null
    } catch (err) {
      ctx.emailTags.value = applyTagRemoved(ctx.emailTags.value || [], tempId)
      console.error('[emailStore] createTag error:', err)
      return null
    }
  }

  ctx.deleteTag = async function deleteTag(tagId) {
    try {
      await ctx.apiDelete(buildEmailApiPath(emailApiBase(), 'tags', tagId))
      const id = Number(tagId)
      ctx.emailTags.value = ctx.emailTags.value.filter(t => t.id !== id)
      // Tag destroy unlinks connections server-side — strip from list + open thread.
      ctx.emails.value = stripTagFromThreads(ctx.emails.value || [], id)
      if (ctx.thread.value) {
        ctx.thread.value = stripTagFromThread(ctx.thread.value, id)
      }
      const c = { ...(ctx.sidebarCounts.value || {}) }
      delete c[`tag_${id}`]
      ctx.sidebarCounts.value = c
      if (ctx.activeFolder.value === `tag_${id}`) {
        ctx.setActiveFolder('inbox')
      }
      return true
    } catch (err) {
      console.error('[emailStore] deleteTag error:', err)
      return false
    }
  }

  ctx.createFolder = async function createFolder(folderName, mailboxId, folderColor) {
    const resolvedMailboxId = mailboxId ?? ctx.activeMailbox.value
    if (!resolvedMailboxId) {
      console.error('[emailStore] createFolder: mailbox_id required')
      return null
    }

    const name = String(folderName || '').trim()
    if (!name) return null

    // Optimistic sidebar row so the create form can close immediately.
    // Temp ids are negative so they never collide with persisted ids.
    const tempId = -Date.now()
    const optimistic = {
      id: tempId,
      name,
      color: folderColor || null,
      mailbox_id: Number(resolvedMailboxId),
    }
    ctx.emailFolders.value = applyFolderUpserted(ctx.emailFolders.value || [], optimistic)

    try {
      const payload = { name, mailbox_id: resolvedMailboxId }
      if (folderColor) payload.color = folderColor
      const data = await ctx.apiPost(
        buildFoldersPath(emailApiBase()),
        { email_folder: payload }
      )
      if (data?.id) {
        ctx.emailFolders.value = applyFolderUpserted(
          applyFolderRemoved(ctx.emailFolders.value || [], tempId),
          {
            id: data.id,
            name: data.name,
            color: data.color,
            mailbox_id: data.mailbox_id,
          }
        )
        return data
      }
      ctx.emailFolders.value = applyFolderRemoved(ctx.emailFolders.value || [], tempId)
      return null
    } catch (err) {
      ctx.emailFolders.value = applyFolderRemoved(ctx.emailFolders.value || [], tempId)
      console.error('[emailStore] createFolder error:', err)
      return null
    }
  }

  ctx.renameFolder = async function renameFolder(folderId, name) {
    const trimmed = (name || '').trim()
    if (!trimmed) return null
    const id = Number(folderId)
    const prev = ctx.emailFolders.value || []
    ctx.emailFolders.value = applyFolderUpserted(prev, {
      ...(prev.find(f => Number(f.id) === id) || { id, mailbox_id: ctx.activeMailbox.value }),
      id,
      name: trimmed,
    })
    try {
      const data = await ctx.apiPatch(
        buildEmailApiPath(emailApiBase(), 'folders', folderId),
        { email_folder: { name: trimmed } }
      )
      if (data?.id) {
        ctx.emailFolders.value = applyFolderUpserted(ctx.emailFolders.value || [], {
          id: data.id,
          name: data.name,
          color: data.color,
          mailbox_id: data.mailbox_id,
        })
        return data
      }
      ctx.emailFolders.value = prev
      return null
    } catch (err) {
      ctx.emailFolders.value = prev
      console.error('[emailStore] renameFolder error:', err)
      return null
    }
  }

  ctx.renameTag = async function renameTag(tagId, tagName, tagColor) {
    const trimmed = (tagName || '').trim()
    if (!trimmed) return null
    const id = Number(tagId)
    const prev = ctx.emailTags.value || []
    const existing = prev.find(t => Number(t.id) === id)
    const optimistic = {
      ...(existing || { id, mailbox_id: ctx.activeMailbox.value }),
      id,
      tag: trimmed,
    }
    if (tagColor !== undefined) optimistic.color = tagColor
    ctx.emailTags.value = applyTagUpserted(prev, optimistic)
    try {
      const payload = { tag: trimmed }
      if (tagColor !== undefined) payload.color = tagColor
      const data = await ctx.apiPatch(
        buildEmailApiPath(emailApiBase(), 'tags', tagId),
        { email_tag: payload }
      )
      if (data?.id) {
        ctx.emailTags.value = applyTagUpserted(ctx.emailTags.value || [], {
          id: data.id,
          tag: data.tag,
          color: data.color,
          mailbox_id: data.mailbox_id,
        })
        return data
      }
      ctx.emailTags.value = prev
      return null
    } catch (err) {
      ctx.emailTags.value = prev
      console.error('[emailStore] renameTag error:', err)
      return null
    }
  }

  ctx.deleteFolder = async function deleteFolder(folderId) {
    try {
      await ctx.apiDelete(buildEmailApiPath(emailApiBase(), 'folders', folderId))
      ctx.emailFolders.value = ctx.emailFolders.value.filter(f => f.id !== Number(folderId))
      if (ctx.activeFolder.value === `folder_${folderId}`) {
        ctx.setActiveFolder('inbox')
      }
      return true
    } catch (err) {
      console.error('[emailStore] deleteFolder error:', err)
      return false
    }
  }

  // Drafts are EmailDraft rows. Their ids must not be sent as email ids
  // (independent sequences — keys can collide). Archive/trash/spam destroy them.
  const DRAFT_DISCARD_ACTIONS = new Set(['archive', 'trash', 'spam'])

  ctx.bulkUpdate = async function bulkUpdate(ids, bulkAction, extraParams = {}) {
    const selected = ids.map(Number)
    const inDraftsFolder = ctx.activeFolder.value === 'drafts'
    const rowsById = new Map((ctx.emails.value || []).map(e => [Number(e.id), e]))
    const draftIds = []
    const emailIds = []
    selected.forEach(id => {
      const row = rowsById.get(id)
      if (inDraftsFolder || row?.is_draft) draftIds.push(id)
      else emailIds.push(id)
    })

    const discardingDrafts = draftIds.length > 0 && DRAFT_DISCARD_ACTIONS.has(bulkAction)
    if (emailIds.length === 0 && !discardingDrafts) {
      ctx.clearSelection()
      return
    }

    const affectedEmails = emailIds.map(id => rowsById.get(id)).filter(Boolean)

    try {
      const payload = {
        ids: emailIds,
        bulk_action: bulkAction,
        ...extraParams,
      }
      if (discardingDrafts) payload.draft_ids = draftIds

      const data = await ctx.apiPatch(buildBulkPath(emailApiBase()), payload)

      const targetFolderId = extraParams.email_folder_id || null
      const removeFromView = data.removed_from_view
      const discarded = (data.discarded_draft_ids || []).map(Number)

      affectedEmails.forEach(e => {
        const before = ctx._threadFacts(e)
        const after = { ...before }

        switch (bulkAction) {
          case 'archive':
            after.archived = true
            after.trashed = false
            after.spam = false
            break
          case 'trash':
            after.trashed = true
            after.spam = false
            break
          case 'spam':
            after.spam = true
            after.archived = false
            after.trashed = false
            after.emailFolderId = null
            break
          case 'not_spam':
            after.spam = false
            break
          case 'restore':
            after.archived = false
            after.trashed = false
            after.spam = false
            break
          case 'unread':
            after.unread = true
            break
          case 'read':
            after.unread = false
            break
          case 'star':
            after.starred = true
            break
          case 'unstar':
            after.starred = false
            break
          case 'move_to_folder':
            after.emailFolderId = targetFolderId ? Number(targetFolderId) : null
            after.archived = false
            after.trashed = false
            after.spam = false
            break
          default:
            return
        }

        ctx._applyCountTransition(before, after, { skipListMeta: !!removeFromView })
      })

      if (discarded.length) {
        ctx._adjustCount('drafts', -discarded.length)
        if (ctx.draftId?.value != null && discarded.includes(Number(ctx.draftId.value))) {
          ctx.closeCompose?.()
        }
      }

      if (removeFromView) {
        emailIds.forEach(id => ctx._removeEmailRow(id))
        discarded.forEach(id => ctx._removeEmailRow(id))
        const openId = Number(ctx.currentThreadId.value)
        if (openId && (emailIds.includes(openId) || discarded.includes(openId))) {
          ctx.clearCurrentThread()
        }
      } else if (data.threads || data.emails) {
        (data.threads || data.emails).forEach(e => ctx._updateEmailRow(normalizeListRow(e)))
      }
      ctx.clearSelection()
      if (['archive', 'trash', 'spam', 'not_spam', 'restore', 'move_to_folder', 'read', 'unread'].includes(bulkAction)) {
        // Don't reuse a count pull that started before this mutation committed.
        ctx._sidebarCountsInflight = null
        ctx.fetchSidebarCounts()
      }
    } catch (err) {
      console.error('[emailStore] bulkUpdate error:', err)
    }
  }


  // ── Sidebar mutators ─────────────────────────────────────────────────────

  ctx.setActiveFolder = function setActiveFolder(folder) {
    // Legacy personal_* folder keys are ignored — personal peers use mailbox_id.
    if (typeof folder === 'string' && folder.startsWith('personal_')) return

    ctx.batch(() => {
      ctx.activeFolder.value = folder
      ctx.currentThreadId.value = null
      ctx.thread.value = null
      ctx.selectedIds.value = []
      if (ctx.composeOpen.value) {
        ctx.composeOpen.value = false
        ctx.draftId.value = null
        ctx.composeDirty.value = false
        ctx.composeDraftData.value = null
        ctx.composeOriginalEmailId.value = null
      }
    })
    ctx.fetchEmails(1)
    ctx.fetchSidebarCounts()
    if (ctx.usesStackedNav()) ctx.activePanel.value = 'list'
    ctx.syncInboxUrl()
  }

  ctx.setActiveMailbox = function setActiveMailbox(mailboxId) {
    ctx.batch(() => {
      ctx.activeMailbox.value = mailboxId ? Number(mailboxId) : null
      ctx.currentThreadId.value = null
      ctx.thread.value = null
      ctx.selectedIds.value = []
    })
    ctx.fetchEmails(1)
    ctx.fetchSidebarCounts()
    if (ctx.usesStackedNav()) ctx.activePanel.value = 'list'
    ctx.syncInboxUrl()
  }


  // ── Bootstrap from data attributes on the shell div ──────────────────────

  ctx.bootstrap = function bootstrap({ mailboxesData, emailFoldersData, emailTagsData, peopleWithUserData, sidebarCountsData, initialFolder, canEditFlag, canImapImportFlag, threadOnlyFlag, storageQuota: storageQuotaPayload }) {
    ctx.batch(() => {
      if (Array.isArray(mailboxesData))         ctx.mailboxes.value         = mailboxesData
      if (Array.isArray(emailFoldersData))      ctx.emailFolders.value      = emailFoldersData
      if (Array.isArray(emailTagsData))         ctx.emailTags.value         = emailTagsData
      if (Array.isArray(peopleWithUserData))    ctx.peopleWithUser.value    = peopleWithUserData
      if (sidebarCountsData && typeof sidebarCountsData === 'object') {
        ctx.sidebarCounts.value = flattenSidebarCountsPayload(sidebarCountsData)
      }
      if (initialFolder)                        ctx.activeFolder.value      = initialFolder
      if (canEditFlag !== undefined)            ctx.canEdit.value           = !!canEditFlag
      if (canImapImportFlag !== undefined)      ctx.canImapImport.value     = !!canImapImportFlag
      if (threadOnlyFlag)                       ctx.threadOnlyView.value    = true
      if (storageQuotaPayload)                  ctx.storageQuota.value      = storageQuotaPayload
    })
  }

  // ── Sidebar counts (pull) ─────────────────────────────────────────────────

  ctx.fetchSidebarCounts = async function fetchSidebarCounts() {
    if (ctx._sidebarCountsInflight) return ctx._sidebarCountsInflight

    const seq = ++ctx._sidebarCountsSeq
    ctx._sidebarCountsInflight = (async () => {
      try {
        const mailboxId = ctx.activeMailbox.value
        const url = buildSidebarCountsPath(
          emailApiBase(),
          mailboxId != null && mailboxId !== '' ? { mailboxId: Number(mailboxId) } : undefined
        )
        const data = await ctx.apiGet(url)
        if (seq !== ctx._sidebarCountsSeq) return
        // v1 renders the flat counts hash directly; Hotwire wrapped it in `counts`.
        const counts = data?.counts || data
        if (counts) ctx.sidebarCounts.value = flattenSidebarCountsPayload(counts)
      } catch (_) {
        // Silent — non-critical
      }
    })().finally(() => {
      ctx._sidebarCountsInflight = null
    })

    return ctx._sidebarCountsInflight
  }
}
